import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import OnboardingWizard from "@/components/OnboardingWizard";
import { LayoutDashboard, FolderKanban, Bell, Beaker, ScanLine, Settings, LogOut, ShieldCheck, Sparkles, AlertOctagon, GitBranch, ClipboardList, GraduationCap, FileText, AlertTriangle, Users, UserCog, ShieldAlert, MessageSquareWarning, FlaskConical, Cpu, Shield, FileArchive, Code, BookUser, Gauge, UsersRound, Globe, Tag, BarChart3, GitMerge, RefreshCw, UserPlus, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import logger from "@/lib/logger";

const NAV = [
  { section: "Overview" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-dashboard" },
  { to: "/quality-kpis", label: "Quality KPIs", icon: BarChart3, testId: "sidebar-quality-kpis" },
  { to: "/traceability", label: "Traceability", icon: GitMerge, testId: "sidebar-traceability" },
  { to: "/readiness", label: "Readiness Score", icon: Gauge, testId: "sidebar-readiness" },
  { to: "/devices", label: "Devices", icon: FolderKanban, testId: "sidebar-devices" },
  { to: "/ocr", label: "OCR Scan", icon: ScanLine, testId: "sidebar-ocr" },
  { section: "Quality Events" },
  { to: "/capa", label: "CAPA", icon: ClipboardList, testId: "sidebar-capa" },
  { to: "/deviations", label: "Deviations", icon: AlertOctagon, testId: "sidebar-deviations" },
  { to: "/changes", label: "Change Control", icon: GitBranch, testId: "sidebar-changes" },
  { to: "/complaints", label: "Complaints / PMS", icon: MessageSquareWarning, testId: "sidebar-complaints" },
  { section: "Documents & Training" },
  { to: "/documents", label: "Documents", icon: FileText, testId: "sidebar-documents" },
  { to: "/training", label: "Training / LMS", icon: GraduationCap, testId: "sidebar-training" },
  { to: "/risk", label: "Risk (ISO 14971)", icon: AlertTriangle, testId: "sidebar-risk" },
  { to: "/risk-register", label: "Risk Register", icon: ShieldAlert, testId: "sidebar-risk-register" },
  { to: "/cybersecurity", label: "Cybersecurity", icon: Shield, testId: "sidebar-cybersecurity" },
  { to: "/nis2", label: "NIS2 (EU)", icon: Shield, testId: "sidebar-nis2" },
  { to: "/ai-governance", label: "AI/ML Governance", icon: Cpu, testId: "sidebar-ai-governance" },
  { to: "/iec62304", label: "IEC 62304", icon: Code, testId: "sidebar-iec62304" },
  { to: "/iec62366", label: "IEC 62366-1 Usability", icon: UsersRound, testId: "sidebar-iec62366" },
  { to: "/submissions", label: "510(k) Bundle", icon: FileArchive, testId: "sidebar-submissions" },
  { to: "/udi", label: "UDI Registry", icon: Tag, testId: "sidebar-udi" },
  { to: "/global-standards", label: "Global Standards", icon: Globe, testId: "sidebar-global-standards" },
  { to: "/training/matrix", label: "Training Matrix", icon: BookUser, testId: "sidebar-training-matrix" },
  { to: "/gap-detector", label: "Gap Detector", icon: ShieldAlert, testId: "sidebar-gap" },
  { to: "/traceability/traceback", label: "Failure Traceback", icon: GitBranch, testId: "sidebar-traceback" },
  { section: "Person-Centred" },
  { to: "/patient-advisory", label: "Patient Advisory", icon: Users, testId: "sidebar-patient" },
  { to: "/clinical", label: "Clinical / eCRF", icon: FlaskConical, testId: "sidebar-clinical" },
  { to: "/alerts", label: "Reg. Alerts", icon: Bell, testId: "sidebar-alerts" },
  { to: "/vitacyt", label: "VitaCyt POC", icon: Beaker, testId: "sidebar-vitacyt" },
  { section: "Admin" },
  { to: "/admin", label: "Users & Roles", icon: UserCog, testId: "sidebar-admin" },
  { to: "/audit/nb-portfolio", label: "NB Portfolio", icon: Building2, testId: "sidebar-nb-portfolio" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "sidebar-settings" },
];

export default function DashboardLayout({ children }) {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = (user?.roles || []).includes("Admin");

  // Beta Progress Badge — poll the pending-leads count so admins never lose
  // sight of interested testers waiting in the marketing pipeline. Refreshes
  // every 60s and immediately on route change so approving a lead updates it.
  const [pendingLeads, setPendingLeads] = useState(0);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    const load = () => {
      api.get("/admin/marketing-leads/pending-count")
        .then(({ data }) => { if (alive) setPendingLeads(data?.pending || 0); })
        .catch((e) => logger.warn("pending-leads count:", e?.response?.status));
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin, loc.pathname]);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const doRefreshRoles = async () => {
    try {
      await refresh();
      toast.success("Session reloaded — your latest roles are now active");
    } catch {
      toast.error("Could not reload session — please log out and back in");
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 vq-sidebar hidden md:flex flex-col p-4">
        <Link to="/dashboard" className="flex items-center gap-2 px-2 py-3 mb-4">
          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-slate-900" />
          </div>
          <div>
            <div className="font-display font-extrabold text-white text-lg leading-none">VitaQMS</div>
            <div className="text-[10px] font-mono text-slate-400 mt-0.5">by PerCen360</div>
          </div>
        </Link>

        <nav className="space-y-1 flex-1 overflow-y-auto pr-1 mt-2">
          {NAV.map((n, idx) => {
            if (n.section) {
              return (
                <div key={`sec-${idx}`} className="vq-overline text-slate-500 px-3 mt-4 mb-1 text-[9px]">
                  {n.section}
                </div>
              );
            }
            const active = loc.pathname === n.to || (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
            const showBetaBadge = isAdmin && n.to === "/admin" && pendingLeads > 0;
            return (
              <Link key={n.to} to={n.to} data-testid={n.testId} className={`vq-sidebar-item ${active ? "active" : ""}`}>
                <n.icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {showBetaBadge && (
                  <span
                    data-testid="sidebar-beta-progress-badge"
                    title={`${pendingLeads} marketing lead${pendingLeads === 1 ? "" : "s"} waiting for approval`}
                    className="ml-auto text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-slate-900 flex items-center gap-1 leading-none"
                  >
                    <UserPlus className="w-2.5 h-2.5" />
                    {pendingLeads}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {user?.plan === "free" && (
          <Link to="/pricing" className="block p-4 rounded-lg bg-white/5 border border-white/10 mb-4 hover:bg-white/10 transition-colors">
            <Sparkles className="w-4 h-4 text-[#0055FF] mb-2" />
            <div className="font-display font-bold text-white text-sm">Upgrade to Pro</div>
            <div className="text-xs text-slate-400 mt-1">Unlock full QMS & alerts</div>
          </Link>
        )}

        <div className="border-t border-white/10 pt-4 flex items-center gap-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-slate-700 text-white text-xs">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-[10px] font-mono text-slate-400 truncate">{user?.email}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1" data-testid="sidebar-roles">
              {(user?.roles || []).length === 0 ? (
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border border-rose-400/30">
                  no role
                </span>
              ) : (
                (user?.roles || []).slice(0, 3).map((role) => (
                  <span key={role} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                    {role}
                  </span>
                ))
              )}
              <button
                onClick={doRefreshRoles}
                data-testid="sidebar-reload-session"
                title="Reload my session (pick up newly-assigned roles without logging out)"
                className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 hover:text-white transition-colors flex items-center gap-1"
              >
                <RefreshCw className="w-2.5 h-2.5" />
                <span>reload</span>
              </button>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="p-2 hover:bg-white/5 rounded-md transition-colors">
            <LogOut className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden vq-nav p-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center">
              <ShieldCheck className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-display font-extrabold">VitaQMS</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="mobile-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </main>
      {/* One-time onboarding wizard — auto-opens for fresh Admins whose
          workspace still has defaults. Non-Admin roles see nothing. */}
      <OnboardingWizard />
    </div>
  );
}
